import { useCallback, useRef } from "react";
import { Phone } from "lucide-react";

import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import TwilioPhoneNumberService from "services/TwilioPhoneNumber";
import {
  useTwilioPhoneNumbersTable,
  invalidateTwilioPhoneNumbersTableQuery,
} from "hooks/useTwilioPhoneNumbersTable";
import { TwilioPhoneNumbersTableActionButtons } from "./components/TwilioPhoneNumbersTableActionButtons";
import AssignAgentsModal from "./components/AssignAgentsModal";
import AssignVoicemailModal from "./components/AssignVoicemailModal";
import AssignHoldMessageModal from "./components/AssignHoldMessageModal";

export const TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS = {
  tableName: "twilio_phone_numbers",
  queryKey: "twilio_phone_numbers",
  header: "Phone Numbers",
  headerIcon: Phone,
};

/**
 * TwilioPhoneNumbersTableV3 component.
 *
 * Displays a table of Twilio phone numbers with support for assigning agents,
 * voicemails and hold messages to phone numbers.
 *
 * @returns {JSX.Element} The TwilioPhoneNumbersTableV3 component
 */
const TwilioPhoneNumbersTableV3 = () => {
  const assignAgentsRef = useRef(null);
  const assignVoicemailRef = useRef(null);
  const assignHoldMessageRef = useRef(null);

  const handleAssignAgents = useCallback((twilioPhoneNumberIds, userIds) => {
    assignAgentsRef.current.open(twilioPhoneNumberIds, userIds);
  }, []);

  const handleAssignVoicemail = useCallback(
    (twilioPhoneNumberId, voicemailId) => {
      assignVoicemailRef.current.open(twilioPhoneNumberId, voicemailId);
    },
    []
  );

  const handleAssignHoldMessage = useCallback(
    (twilioPhoneNumberId, holdMessage) => {
      assignHoldMessageRef.current.open(twilioPhoneNumberId, holdMessage);
    },
    []
  );

  const { filters, columns } = useTwilioPhoneNumbersTable({
    tableName: TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS.tableName,
    handleAssignAgents,
    handleAssignVoicemail,
    handleAssignHoldMessage,
  });

  const fetchTwilioPhoneNumbers = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        const response =
          await TwilioPhoneNumberService.getTable(nextQueryParams);
        return response;
      } catch (error) {
        console.error("Error fetching twilio phone numbers:", error);
        throw error;
      }
    },
    [columns]
  );

  const actionButtons = useCallback(
    ({ selectedIds, selectedData }) => (
      <TwilioPhoneNumbersTableActionButtons
        selectedIds={selectedIds}
        selectedData={selectedData}
        handleAssignAgents={handleAssignAgents}
      />
    ),
    [handleAssignAgents]
  );

  return (
    <>
      <AssignAgentsModal
        ref={assignAgentsRef}
        onSuccess={invalidateTwilioPhoneNumbersTableQuery}
      />
      <AssignVoicemailModal
        ref={assignVoicemailRef}
        onSuccess={invalidateTwilioPhoneNumbersTableQuery}
      />
      <AssignHoldMessageModal
        ref={assignHoldMessageRef}
        onSuccess={invalidateTwilioPhoneNumbersTableQuery}
      />
      <BaseTableV3
        columns={columns}
        filters={filters}
        queryKey={TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS.queryKey}
        tableName={TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS.tableName}
        header={TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS.header}
        headerIcon={TWILIO_PHONE_NUMBERS_TABLE_CONSTANTS.headerIcon}
        enableSearch={false}
        enableSettings
        fetchData={fetchTwilioPhoneNumbers}
        actionButtons={actionButtons}
        invalidateData={invalidateTwilioPhoneNumbersTableQuery}
      />
    </>
  );
};

export default TwilioPhoneNumbersTableV3;
